import { createSlice } from '@reduxjs/toolkit';
import {
  getContacts,
  postValidateContactChannel,
  postCreateMessageContact,
} from './services';
import Trans from '@/components/shared/Trans';
import senderTranslations from '@/components/Sender/Sender.trans.json';
import { EXTERNAL_NETWORKS } from '@/utils/networkConstants';

const computeInitialMessage = (outerNet, propertiesIds) => {
  const isQuintoAndar =
    outerNet === EXTERNAL_NETWORKS.QUINTOANDAR && propertiesIds.length > 0;
  return isQuintoAndar
    ? Trans(senderTranslations, 'QuintoAndarSelectedPropertiesWhatsapp')
    : '';
};

// Initial state
const initialState = {
  show: false,
  loading: false,
  contacts: [],
  contactSelected: null,
  preSelectedPropertiesIds: [],
  outerNet: null, // null = Tokko, string = external network identifier
  fromOuterNet: false, // backward compatibility
  initialMessage: '',
  validateChannel: {
    loading: false,
    hasActiveChat: false,
    error: null,
    agentName: null,
  },
  sendMessage: {
    loading: false,
    success: false,
    error: null,
  },
};

export const SharePropertiesMultipleSourcesSlice = createSlice({
  name: 'SharePropertiesMultipleSources',
  initialState,
  reducers: {
    setShowSharePropertiesMultipleSources: (state, action) => {
      state.show = action.payload;
      if (!action.payload) {
        Object.assign(state, initialState);
      }
    },
    setContactSelected: (state, action) => {
      state.contactSelected = action.payload;
    },
    setPreSelectedPropertiesIds: (state, action) => {
      state.preSelectedPropertiesIds = action.payload;
      // recompute initial message whenever properties change
      state.initialMessage = computeInitialMessage(
        state.outerNet,
        action.payload
      );
    },
    setFromOuterNet: (state, action) => {
      state.fromOuterNet = action.payload;
    },
    setOuterNet: (state, action) => {
      state.outerNet = action.payload;
      // recompute initial message whenever outerNet changes
      state.initialMessage = computeInitialMessage(
        action.payload,
        state.preSelectedPropertiesIds
      );
    },
  },
  extraReducers: (builder) => {
    builder // getContacts
      .addCase(getContacts.pending, (state) => {
        state.loading = true;
      })
      .addCase(getContacts.fulfilled, (state, action) => {
        const isPaginating = action.meta.arg.page > 1;

        state.contacts = isPaginating
          ? [...state.contacts, ...action.payload.contacts]
          : action.payload.contacts;

        state.loading = false;
      })
      .addCase(getContacts.rejected, (state) => {
        state.contacts = [];
        state.loading = false;
      });

    builder // postValidateContactChannel
      .addCase(postValidateContactChannel.pending, (state) => {
        state.validateChannel.loading = true;
        state.validateChannel.hasActiveChat = false;
        state.validateChannel.error = null;
      })
      .addCase(postValidateContactChannel.fulfilled, (state, action) => {
        const { cid, is_mine, member_name } = action.payload || {};

        state.validateChannel.loading = false;
        state.validateChannel.agentName = member_name;

        if (cid && is_mine === false) {
          state.validateChannel.hasActiveChat = true;
        } else {
          state.validateChannel.hasActiveChat = false;
        }
      })
      .addCase(postValidateContactChannel.rejected, (state, action) => {
        state.validateChannel.loading = false;
        state.validateChannel.error = action.payload || 'Validation failed';
      });

    builder // postCreateMessageContact
      .addCase(postCreateMessageContact.pending, (state) => {
        state.sendMessage.loading = true;
        state.sendMessage.success = false;
        state.sendMessage.error = null;
      })
      .addCase(postCreateMessageContact.fulfilled, (state) => {
        state.sendMessage.loading = false;
        state.sendMessage.success = true;
      })
      .addCase(postCreateMessageContact.rejected, (state, action) => {
        state.sendMessage.loading = false;
        state.sendMessage.error = action.payload || 'Failed to send message';
      });
  },
});

export const {
  setShowSharePropertiesMultipleSources,
  setContactSelected,
  setPreSelectedPropertiesIds,
  setFromOuterNet,
  setOuterNet,
} = SharePropertiesMultipleSourcesSlice.actions;

export default SharePropertiesMultipleSourcesSlice.reducer;
